/* =========================================================================
   BRAIN CONSOLE — admin screen to operate the brain (Phase 2a UI).
   Built on the new design kit (src/design). Talks to /api/brain.
   Lets admin: initialize the schema, backfill from the legacy KB, see stats,
   and live-test semantic retrieval. First screen on the refined-SaaS kit.
   ========================================================================= */
import React, { useState, useEffect, useCallback } from "react";
import { T } from "./design/tokens";
import { Button, Card, Pill, Tag, Tabs, Input, Select, StatCard, Caps, Num } from "./design/kit";
// [WPM1 2026-08-10] the central model config — see src/pipeline/model-config.js
import { AI_MODEL_DEFAULT, aiEndpoint, aiRefusal, aiText, buildAiBody, parseAiJson, parseModelId } from "./pipeline/model-config";
import { BRAIN_BASIS_CATEGORIES, JURISDICTION_AUDIT_PROMPT } from "./brain-jurisdiction";

const adminToken = () => localStorage.getItem("ez-wins-admin-token") || "";
async function brainFetch(action, body = {}) {
  const res = await fetch("/api/brain", {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-team-token": adminToken() },
    body: JSON.stringify({ action, ...body }),
  });
  const raw = await res.text();
  let data = {};
  try { data = raw ? JSON.parse(raw) : {}; } catch { /* non-JSON (platform error page) */ }
  if (!res.ok) throw new Error(data.detail || data.error || `HTTP ${res.status} — ${(raw || "(empty body)").slice(0, 400)}`);
  return data;
}

// [#7 2026-06-11] AI-assisted principle distillation. Calls the Anthropic proxy (admin token accepted),
// adaptive thinking, and pulls the JSON array out of the text block. Used to draft candidate principles
// from clusters of confirmed precedents — drafts do nothing until an admin Approves them (existing gate).
const DISTILL_SYS = `You are distilling REUSABLE CLASSIFICATION PRINCIPLES from a cluster of human-confirmed repair-line decisions for California warranty retail-rate justification.

You are given confirmed precedents that share a theme (the category is provided in the input). Each has the repair (component / action / reason), the human's decision (Include or Exclude), and the human's rationale.

Propose 0-3 SHORT, GENERAL principles a classifier could apply to NEW lines — the shared rule-of-thumb behind these decisions, not a restatement of any single case. A good principle: captures WHY these were decided that way (the qualifying or disqualifying reason) in 1-2 sentences; is general enough for unseen repairs of the same shape but specific enough to act on; names the decision lean it supports; never contradicts California Vehicle Code §3065 or the explicit override categories (collision / theft / rodent / elements / vandalism / insurance / service-contract / goodwill / human-intervention).

Do NOT invent principles the cluster does not support. If the cluster has no clear shared rule, return an empty array.

Output ONLY a JSON array: [{"title": short label, "body": the principle (1-2 sentences), "decisionLean": "Include"|"Exclude", "basis": short note on which cases support it, "confidence": "high"|"medium"|"low"}].`;

// [WPM1 2026-08-10] The Brain console's two AI calls take their model from the central config, so
// a model change is still one edit. They deliberately ignore the run's `ai-model` arm:
// distillation and enrichment write into the shared brain, and an A/B arm set in a browser tab
// must not silently decide which model authored a stored principle.
// [WPM1-R3 — Blake's ruling 2026-08-11] Authorship FOLLOWS THE MAIN MODEL: "the model that
// performs best classification-wise should also be used as authorship … the default should be
// the Main model." The reviewer's one-voice concern was weighed and overruled — the anatomy
// card is a rigid form (component/action/reason/disqualifiers), so the author-style surface is
// small, and principles pass Blake's approval gate regardless of who drafted them. What still
// holds: a browser tab's `ai-model` A/B arm NEVER decides authorship — only the shipped default.
async function brainAiRaw(system, userObj) {
  const { provider, model } = parseModelId(AI_MODEL_DEFAULT);
  const res = await fetch(aiEndpoint(provider), {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-team-token": adminToken() },
    body: JSON.stringify(buildAiBody({ provider, model, system, userText: JSON.stringify(userObj) })),
  });
  if (!res.ok) throw new Error(`${provider} ${res.status}: ${(await res.text()).slice(0, 200)}`);
  return { provider, data: await res.json() };
}

async function claudeDistill(userObj) {
  const { provider, data } = await brainAiRaw(DISTILL_SYS, userObj);
  // A refusal must never be parsed as content — aiRefusal is the guard; no principles is the
  // correct outcome, and the distiller already treats "no clear shared rule" as an empty array.
  if (aiRefusal(provider, data)) { console.warn("Distiller batch refused"); return []; }
  const txt = aiText(provider, data);
  const m = txt.match(/\[[\s\S]*\]/);
  return m ? JSON.parse(m[0]) : [];
}

async function tagJurisdictionBatch(entries) {
  const { provider, data } = await brainAiRaw(JURISDICTION_AUDIT_PROMPT, entries);
  if (aiRefusal(provider, data)) throw new Error("Jurisdiction audit batch was refused");
  const parsed = parseAiJson(provider, data);
  if (!Array.isArray(parsed) || parsed.length !== entries.length) throw new Error("Jurisdiction audit returned the wrong number of rows");
  return parsed;
}

// [CANON-2E2 2026-06-10] Meaning layer. Canonical summary = component | action | reason | disqualifiers
// — deliberately NO `system` (kept a light signal per Blake). Same builder will feed query-side later.
export const buildCanonical = (s) => {
  if (!s) return "";
  const core = [s.component, s.action, s.reason].map(x => (x || "").toString().trim().toLowerCase()).filter(Boolean);
  const dq = Array.isArray(s.disqualifiers) ? s.disqualifiers.filter(d => d && d !== "none") : [];
  return [...core, ...dq].join(" | ");
};
// [CANON-2E2 2c] soft category bucket = most decision-relevant disqualifier (else "qualifying").
const CATEGORY_PRIORITY = ["human-intervention", "insurance", "collision", "road-hazard", "vandalism", "theft", "rodent", "elements", "negligence", "aftermarket", "goodwill", "government", "fleet", "accessory", "tire", "glass", "maintenance", "wear"];
export const deriveCategory = (s) => {
  const dq = Array.isArray(s?.disqualifiers) ? s.disqualifiers.filter(d => d && d !== "none") : [];
  for (const c of CATEGORY_PRIORITY) if (dq.includes(c)) return c;
  return dq[0] || "qualifying";
};
const EXTRACT_SYS = `You extract the repair ANATOMY from one auto repair-order line — describe what the repair IS, not whether to include it.
For each input {id, correction, cause, complaint} output: {id, component: short noun (e.g. "alternator","axle boot","multifunction switch"), action: replaced|repaired|diagnosed_only|adjusted|programmed|cleaned|inspected|reconditioned|other, reason: SHORT phrase for WHY it was done — the trigger/cause mined from cause/complaint (e.g. "internal failure","road debris","normal wear","prior improper install","no fault found"), disqualifiers: array of any of [collision,theft,wear,maintenance,aftermarket,insurance,goodwill,tire,glass,government,fleet,human-intervention,road-hazard,elements,rodent,vandalism,negligence,accessory] or []}.
If the work was only RECOMMENDED / advised / quoted / "recheck" / no repair performed, use action "diagnosed_only" — never "replaced".
Do NOT tag the "tire" or "glass" disqualifier on their electronic exceptions (TPMS / tire-pressure sensors, rain/light/camera sensors) — those are includable components.
Consent language ("customer request" / "per customer" / "customer states" / "C/S:") is NOISE — never a disqualifier and never "human-intervention". Tag "human-intervention" only when a PERSON actually caused or left the condition (prior improper/incomplete repair, mis-install, customer modification). Tag "wear" only for routine maintenance-interval wear — a component that FAILED is not "wear". Tag "road-hazard" for road debris / pothole / curb impact damage; "elements" for water/flood/weather/corrosion damage from exposure; "negligence" for owner or third-party neglect (e.g. ran without oil, ignored warnings); "accessory" for add-on accessory items (hitches, racks, running boards, lift kits).
Output ONLY a valid JSON array.`;
async function extractAnatomyBatch(rows) {
  const payload = rows.map((r, i) => ({ id: i, correction: r.correction || "", cause: r.cause || "", complaint: r.complaint || "" }));
  const { provider, data } = await brainAiRaw(EXTRACT_SYS, payload);
  // Unparseable OR refused anatomy has always degraded to "no anatomy" rather than failing the
  // enrichment run; parseAiJson raises a refusal, which this catch turns into that same outcome.
  // [WPM1-R1] …but not INVISIBLY: a refused batch is warned, so a run of empty anatomy is
  // diagnosable from the console instead of reading as "the model had nothing to say".
  try { return parseAiJson(provider, data); } catch (e) { console.warn("anatomy batch degraded to empty:", e?.code === "ai_refusal" ? `refused: ${e.detail}` : e); return []; }
}

const PREC_COLS = "92px 120px minmax(260px,1.6fr) minmax(200px,1.2fr) 70px";
const ASSOC_COLS = "92px 120px minmax(360px,2fr) minmax(160px,1fr)";
const PRINC_COLS = "92px 110px minmax(200px,1fr) minmax(300px,2fr)";  // [BRAIN-LOOP1] Match · Lean · Title · Body

export default function BrainConsole() {
  const [stats, setStats] = useState(null);
  const [statErr, setStatErr] = useState("");
  const [busy, setBusy] = useState("");        // "init" | "backfill" | ""
  const [msg, setMsg] = useState(null);        // { ok, text }
  const [q, setQ] = useState("");
  const [type, setType] = useState("precedent");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [section, setSection] = useState("ops");
  const [space, setSpace] = useState("text");        // [CANON-2E2] "text" | "canonical" (meaning)
  const [enrichProg, setEnrichProg] = useState("");
  const [canonQ, setCanonQ] = useState("");          // resolved canonical of the last meaning-search

  const loadStats = useCallback(async () => {
    setStatErr("");
    try { setStats(await brainFetch("stats")); }
    catch (e) { setStats(null); setStatErr(e.message); }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const run = async (action, label) => {
    setBusy(action); setMsg(null);
    try {
      const r = await brainFetch(action);
      setMsg({ ok: true, text: `✓ ${label}: ${JSON.stringify(r)}` });
      await loadStats();
    } catch (e) { setMsg({ ok: false, text: `${label} failed — ${e.message}` }); }
    setBusy("");
  };

  const runDiag = async () => {
    setBusy("diag"); setMsg(null);
    try { const r = await brainFetch("env-check"); setMsg({ ok: true, text: `env-check OK → ${JSON.stringify(r)}` }); }
    catch (e) { setMsg({ ok: false, text: `env-check failed — ${e.message}` }); }
    setBusy("");
  };

  const doSearch = async () => {
    if (!q.trim()) return;
    setSearching(true); setResults(null); setCanonQ("");
    try {
      let text = q.trim(), useSpace = "text", boostCategory = "";
      if (space === "canonical" && type === "precedent") {  // [CANON-2E2] meaning search: extract the query's anatomy → canonical → search meaning space (+ bucket boost)
        const anat = await extractAnatomyBatch([{ correction: q.trim() }]);
        const canon = buildCanonical(anat[0] || {});
        if (canon) { text = canon; useSpace = "canonical"; boostCategory = deriveCategory(anat[0] || {}); setCanonQ(`${canon}${boostCategory && boostCategory !== "qualifying" ? `   ·   bucket: ${boostCategory}` : ""}`); }
      }
      const r = await brainFetch("search", { text, type, k: 10, space: useSpace, boostCategory });
      setResults(r.results || []);
    } catch (e) { setMsg({ ok: false, text: `Search failed — ${e.message}` }); }
    setSearching(false);
  };

  // [CANON-2E2] Build the meaning index: extract anatomy for every precedent (client-side AI),
  // build the canonical string, and embed it into the brain. One-time / re-runnable. Observational —
  // does NOT change live classification; it just gives the precedents a meaning-space representation.
  // [AUDIT 2026-06-10] Export all precedents (incl. canonical/category/decision) as JSON — for offline triage.
  const exportPrecedents = async () => {
    setBusy("export"); setMsg(null);
    try {
      const { rows } = await brainFetch("list", { type: "precedent" });
      const data = rows || [];
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `brain-precedents-${data.length}.json`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setMsg({ ok: true, text: `✓ Exported ${data.length} precedents to brain-precedents-${data.length}.json` });
    } catch (e) { setMsg({ ok: false, text: `Export failed — ${e.message}` }); }
    setBusy("");
  };

  const buildMeaningIndex = async () => {
    setBusy("enrich"); setMsg(null); setEnrichProg("");
    try {
      const { rows } = await brainFetch("list", { type: "precedent" });
      const precs = (rows || []).filter(r => r && r.id);
      let done = 0; const BATCH = 12;
      for (let i = 0; i < precs.length; i += BATCH) {
        const chunk = precs.slice(i, i + BATCH);
        setEnrichProg(`Extracting ${i + 1}–${Math.min(i + BATCH, precs.length)} of ${precs.length}…`);
        let anat = []; try { anat = await extractAnatomyBatch(chunk); } catch { anat = []; }
        const items = chunk.map((p, j) => { const s = anat.find(a => a && a.id === j) || {}; return { id: p.id, structured: s, canonical: buildCanonical(s), category: deriveCategory(s) }; }).filter(it => it.canonical);
        if (items.length) await brainFetch("enrich-batch", { items });
        done += items.length;
      }
      setEnrichProg("");
      setMsg({ ok: true, text: `✓ Meaning index built — enriched ${done} of ${precs.length} precedents.` });
      await loadStats();
    } catch (e) { setEnrichProg(""); setMsg({ ok: false, text: `Build meaning index failed — ${e.message}` }); }
    setBusy("");
  };

  const pct = v => v == null ? "—" : `${Math.round(v * 100)}%`;
  const wrap = { padding: T.space[7], maxWidth: 1320, margin: "0 auto", fontFamily: T.font.sans };

  return (
    <div style={wrap}>
      <div style={{ marginBottom: T.space[6] }}>
        <Caps>Phase 2 · semantic brain</Caps>
        <h1 style={{ margin: "6px 0 0", fontSize: 28, fontWeight: T.weight.bold, color: T.color.text, letterSpacing: "-0.02em" }}>Brain console</h1>
        <div style={{ fontSize: T.size.sm, color: T.color.textMuted, marginTop: 4 }}>
          Operate the semantic brain — initialize, backfill from the legacy KB, and test retrieval. RAG-only: this stores & retrieves; it doesn't change live classification yet.
        </div>
      </div>

      {/* stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: T.space[5], marginBottom: T.space[7] }}>
        <StatCard label="Precedents" value={stats ? stats.precedents.rows : "—"} sub={stats ? `${stats.precedents.confirmations} confirmations · ${stats.precedents.enriched ?? 0} meaning-indexed` : "not initialized"} icon="🧠" accent={T.color.violet} />
        <StatCard label="Associations" value={stats ? stats.associations : "—"} sub="anchor ↔ part pairs" accent={T.color.cyan} />
        <StatCard label="Principles" value={stats ? stats.principles.rows : "—"} sub={stats ? `${stats.principles.approved} approved` : "—"} accent={T.color.accent} />
        <StatCard label="Embeddings" value="1536-d" sub="text-embedding-3-small" accent={T.color.textFaint} />
      </div>

      <div style={{ marginBottom: T.space[6] }}>
        <Tabs value={section} onChange={setSection} tabs={[
          { id: "ops", label: "Operations" },
          { id: "precedents", label: "Precedents", count: stats ? stats.precedents.rows : undefined },
          { id: "associations", label: "Associations", count: stats ? stats.associations : undefined },
          { id: "principles", label: "Principles", count: stats ? stats.principles.rows : undefined },
          { id: "jurisdiction", label: "Jurisdiction audit" },
        ]} />
      </div>

      {section === "ops" && (<>
      {/* operations */}
      <Card raised style={{ marginBottom: T.space[7] }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: T.size.md, fontWeight: T.weight.semibold, color: T.color.text }}>Setup &amp; backfill</div>
            <div style={{ fontSize: T.size.sm, color: T.color.textMuted, marginTop: 3 }}>
              <b>Initialize</b> creates the schema (run once). <b>Backfill</b> imports the legacy KB — dedup → support_count, embeds the holistic CCC. Idempotent &amp; safe to re-run.
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Button variant="ghost" onClick={runDiag} disabled={!!busy}>{busy === "diag" ? "Checking…" : "Diagnostics"}</Button>
            <Button variant="secondary" onClick={() => run("init", "Initialize")} disabled={!!busy}>{busy === "init" ? "Initializing…" : "Initialize schema"}</Button>
            <Button variant="primary" onClick={() => run("backfill", "Backfill")} disabled={!!busy}>{busy === "backfill" ? "Backfilling…" : "Backfill legacy KB"}</Button>
            <Button variant="secondary" onClick={buildMeaningIndex} disabled={!!busy}>{busy === "enrich" ? (enrichProg || "Building…") : "Build meaning index"}</Button>
            <Button variant="ghost" onClick={exportPrecedents} disabled={!!busy}>{busy === "export" ? "Exporting…" : "⬇ Export precedents"}</Button>
          </div>
        </div>
        <div style={{ marginTop: 10, fontSize: T.size.caption, color: T.color.textMuted }}>
          <b>Build meaning index</b> [CANON-2E2]: extracts each precedent's repair anatomy (component·action·reason) and embeds a <i>canonical</i> summary, so retrieval can match repair <i>meaning</i> not wording. Re-runnable; doesn't change live classification.
        </div>
        {stats?.categories?.length > 0 && (
          <div style={{ marginTop: 12, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <Caps>Meaning buckets</Caps>
            {stats.categories.map(c => <Tag key={c.category} color={c.category === "qualifying" ? T.color.include : T.color.textMuted}>{c.category} · {c.n}</Tag>)}
          </div>
        )}
        {msg && (
          <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: T.radius.md, fontFamily: T.font.mono, fontSize: T.size.caption, lineHeight: 1.5, wordBreak: "break-word",
            background: msg.ok ? T.color.includeSoft : T.color.excludeSoft, color: msg.ok ? T.color.include : T.color.exclude, border: `1px solid ${msg.ok ? T.color.includeBorder : T.color.excludeBorder}` }}>
            {msg.text}
          </div>
        )}
        {statErr && !stats && <div style={{ marginTop: 12, fontSize: T.size.caption, color: T.color.textMuted }}>Stats unavailable ({statErr}). If this is the first run, hit <b>Initialize schema</b>.</div>}
      </Card>

      {/* retrieval tester */}
      <div style={{ marginBottom: T.space[5] }}>
        <div style={{ fontSize: T.size.lg, fontWeight: T.weight.semibold, color: T.color.text, letterSpacing: T.tracking.tightNeg }}>Test retrieval</div>
        <div style={{ fontSize: T.size.sm, color: T.color.textMuted, marginTop: 3 }}>Embed a line and pull the nearest brain entries — this is what RAG (2b) will feed the AI.</div>
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: T.space[5], alignItems: "center" }}>
        <div style={{ flex: 1 }}><Input value={q} onChange={e => setQ(e.target.value)} placeholder='e.g. "replaced faulty multifunction switch"' leftIcon="⌕"
          style={{}} /></div>
        <div style={{ width: 180 }}><Select value={type} onChange={e => setType(e.target.value)} options={[{ value: "precedent", label: "Line precedents" }, { value: "association", label: "Associations" }, { value: "principle", label: "Principles" }]} /></div>{/* [BRAIN-LOOP1] eyeball principle relevance before any retrieval ever ships */}
        {type === "precedent" && <div style={{ width: 150 }}><Select value={space} onChange={e => setSpace(e.target.value)} options={[{ value: "text", label: "Text match" }, { value: "canonical", label: "Meaning match" }]} /></div>}
        <Button variant="primary" onClick={doSearch} disabled={searching || !q.trim()}>{searching ? "Searching…" : "Search"}</Button>
      </div>
      {canonQ && <div style={{ marginTop: -8, marginBottom: T.space[5], fontSize: T.size.caption, color: T.color.textMuted, fontFamily: T.font.mono }}>meaning query → <span style={{ color: T.color.accent }}>{canonQ}</span></div>}

      {results && (
        <div style={{ border: `1px solid ${T.color.border}`, borderRadius: T.radius.lg, overflow: "hidden", background: T.color.surface }}>
          {type === "precedent" ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: PREC_COLS, borderBottom: `1px solid ${T.color.border}` }}>
                {["Match", "Decision", "Correction", "Why", "Uses"].map((c, i) => <div key={i} style={{ padding: "11px 14px" }}><Caps>{c}</Caps></div>)}
              </div>
              {results.map((r, i) => (
                <div key={r.id || i} style={{ display: "grid", gridTemplateColumns: PREC_COLS, alignItems: "center", borderBottom: `1px solid ${T.color.borderFaint}` }}>
                  <div style={{ padding: "12px 14px" }}><Num size={T.size.sm} color={r.similarity >= 0.8 ? T.color.include : r.similarity >= 0.6 ? T.color.accent : T.color.textMuted}>{pct(r.similarity)}</Num></div>
                  <div style={{ padding: "12px 14px" }}>{["Include", "Exclude", "Human Review"].includes(r.decision) ? <Pill status={r.decision === "Include" ? "include" : r.decision === "Exclude" ? "exclude" : "review"} size="sm" /> : <Tag>{r.decision || "—"}</Tag>}</div>
                  <div style={{ padding: "12px 14px", fontSize: T.size.sm, color: T.color.text, lineHeight: T.leading.snug }}>{r.correction || <em style={{ color: T.color.textFaint }}>{(r.cause || r.complaint || "blank").slice(0, 60)}</em>}{r.category && r.category !== "qualifying" && <span style={{ marginLeft: 6, fontSize: 10, color: T.color.accent }}>· {r.category}</span>}</div>
                  <div style={{ padding: "12px 14px", fontSize: T.size.caption, color: T.color.textSecondary, lineHeight: T.leading.snug }}>{r.rationale}</div>
                  <div style={{ padding: "12px 14px" }}><Num size={T.size.sm} color={T.color.textMuted}>{r.support_count}</Num></div>
                </div>
              ))}
            </>
          ) : type === "association" ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: ASSOC_COLS, borderBottom: `1px solid ${T.color.border}` }}>
                {["Match", "Relation", "Associate ↔ Anchor", "Why"].map((c, i) => <div key={i} style={{ padding: "11px 14px" }}><Caps>{c}</Caps></div>)}
              </div>
              {results.map((r, i) => (
                <div key={r.id || i} style={{ display: "grid", gridTemplateColumns: ASSOC_COLS, alignItems: "center", borderBottom: `1px solid ${T.color.borderFaint}` }}>
                  <div style={{ padding: "12px 14px" }}><Num size={T.size.sm} color={r.similarity >= 0.8 ? T.color.include : T.color.accent}>{pct(r.similarity)}</Num></div>
                  <div style={{ padding: "12px 14px" }}><Tag color={r.relation === "associated" ? T.color.include : r.relation === "not" ? T.color.exclude : T.color.review}>{r.relation}</Tag></div>
                  <div style={{ padding: "12px 14px", fontSize: T.size.sm, color: T.color.text, lineHeight: T.leading.snug }}>{r.associate_correction} <span style={{ color: T.color.textFaint }}>↔</span> {r.anchor_correction}</div>
                  <div style={{ padding: "12px 14px", fontSize: T.size.caption, color: T.color.textSecondary }}>{r.rationale}</div>
                </div>
              ))}
            </>
          ) : (
            <>{/* [BRAIN-LOOP1] principle relevance results (approved-only) */}
              <div style={{ display: "grid", gridTemplateColumns: PRINC_COLS, borderBottom: `1px solid ${T.color.border}` }}>
                {["Match", "Lean", "Title", "Body"].map((c, i) => <div key={i} style={{ padding: "11px 14px" }}><Caps>{c}</Caps></div>)}
              </div>
              {results.map((r, i) => (
                <div key={r.id || i} style={{ display: "grid", gridTemplateColumns: PRINC_COLS, alignItems: "center", borderBottom: `1px solid ${T.color.borderFaint}` }}>
                  <div style={{ padding: "12px 14px" }}><Num size={T.size.sm} color={r.similarity >= 0.8 ? T.color.include : r.similarity >= 0.6 ? T.color.accent : T.color.textMuted}>{pct(r.similarity)}</Num></div>
                  <div style={{ padding: "12px 14px" }}>{r.decision_lean ? <Tag color={r.decision_lean === "Include" ? T.color.include : r.decision_lean === "Exclude" ? T.color.exclude : T.color.review}>{r.decision_lean}</Tag> : <Tag>—</Tag>}</div>
                  <div style={{ padding: "12px 14px", fontSize: T.size.sm, fontWeight: T.weight.semibold, color: T.color.text, lineHeight: T.leading.snug }}>{r.title || <em style={{ color: T.color.textFaint }}>untitled</em>}</div>
                  <div style={{ padding: "12px 14px", fontSize: T.size.caption, color: T.color.textSecondary, lineHeight: T.leading.snug }}>{r.body}</div>
                </div>
              ))}
            </>
          )}
          {results.length === 0 && <div style={{ padding: 40, textAlign: "center", color: T.color.textMuted, fontSize: T.size.sm }}>No matches — the brain may be empty (run Backfill) or nothing's similar.</div>}
        </div>
      )}
      </>)}

      {section === "precedents" && <BrowseSection type="precedent" onChanged={loadStats} />}
      {section === "associations" && <BrowseSection type="association" onChanged={loadStats} />}
      {section === "principles" && <PrinciplesSection onChanged={loadStats} />}
      {section === "jurisdiction" && <JurisdictionAuditSection />}
    </div>
  );
}

/* ---- Browse / manage brain entries ---- */
// [WPAR3 2026-08-12] Jurisdiction audit — AI proposes deciding-basis tags (AI_MODEL_DEFAULT by the
// brain-authorship law), Blake approves in this console; nothing is consumed unapproved.
function JurisdictionAuditSection() {
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try { const r = await brainFetch("list-jurisdiction-audit"); setRows(r.rows || []); setMsg(""); }
    catch (e) { setRows([]); setMsg(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const audit = async () => {
    const pending = (rows || []).filter(r => !r.basis_category);
    if (!pending.length) { setMsg("Every loaded entry already has a proposal."); return; }
    setBusy("audit"); setMsg("");
    try {
      const BATCH = 12;
      let done = 0;
      for (let i = 0; i < pending.length; i += BATCH) {
        const chunk = pending.slice(i, i + BATCH);
        setMsg(`Tagging ${i + 1}–${Math.min(i + BATCH, pending.length)} of ${pending.length}…`);
        const inputs = chunk.map(r => r.type === "principle" ? {
          id:r.id, type:r.type, title:r.title || "", body:r.body || "", decision:r.decision_lean || "", rationale:"",
          ccc:{ correction:"", cause:"", complaint:"" },
        } : {
          id:r.id, type:r.type, text:r.canonical || "", decision:r.decision || "", rationale:r.rationale || "",
          ccc:{ correction:r.correction || "", cause:r.cause || "", complaint:r.complaint || "" },
        });
        const proposals = await tagJurisdictionBatch(inputs);
        const byId = new Map(proposals.map(p => [p?.id, p]));
        const items = chunk.map(r => {
          const p = byId.get(r.id);
          if (!p || !BRAIN_BASIS_CATEGORIES.includes(p.basisCategory) || !["high", "medium", "low"].includes(p.confidence) || !String(p.why || "").trim()) {
            throw new Error(`Invalid audit proposal for ${r.id}`);
          }
          return { type:r.type, id:r.id, basisCategory:p.basisCategory, basisApproved:false, confidence:p.confidence, why:p.why, model:AI_MODEL_DEFAULT };
        });
        await brainFetch("annotate-jurisdiction", { items, reviewer:"ai-proposal" });
        done += items.length;
      }
      await load(); setMsg(`Tagged ${done} entries. Review is required before any can serve Arkansas.`);
    } catch (e) { setMsg(`Audit stopped — ${e.message}`); }
    setBusy("");
  };

  const save = async (row, approved, category = row.basis_category) => {
    const auditStamp = row.basis_audit || {};
    setBusy(row.id);
    try {
      await brainFetch("annotate-jurisdiction", { items:[{
        type:row.type, id:row.id, basisCategory:category, basisApproved:approved,
        confidence:auditStamp.confidence || "low", why:auditStamp.why || "Reviewed in jurisdiction audit console.", model:auditStamp.model || "human",  /* [WPAR3-R2 2026-08-12] no fabricated provenance — a manual tag was not authored by a model */
      }], reviewer:"admin" });
      await load();
    } catch (e) { setMsg(e.message); }
    setBusy("");
  };

  const approveSelected = async () => {
    const chosen = (rows || []).filter(r => r.type === "precedent" && selected[r.id] && r.basis_category);
    if (!chosen.length) return;
    setBusy("bulk");
    try {
      const items = chosen.map(r => ({ type:r.type, id:r.id, basisCategory:r.basis_category, basisApproved:true,
        confidence:r.basis_audit?.confidence || "low", why:r.basis_audit?.why || "Reviewed in jurisdiction audit console.", model:r.basis_audit?.model || "human" }));  /* [WPAR3-R2 2026-08-12] */
      for (let i = 0; i < items.length; i += 50) await brainFetch("annotate-jurisdiction", { items:items.slice(i, i + 50), reviewer:"admin" });
      setSelected({}); await load(); setMsg(`Approved ${items.length} precedents.`);
    } catch (e) { setMsg(e.message); }
    setBusy("");
  };

  const list = [...(rows || [])].sort((a, b) => String(a.basis_category || "unclassified").localeCompare(String(b.basis_category || "unclassified"))
    || ({ high:0, medium:1, low:2 }[a.basis_audit?.confidence] ?? 3) - ({ high:0, medium:1, low:2 }[b.basis_audit?.confidence] ?? 3));
  return <Card raised>
    <div style={{ display:"flex", justifyContent:"space-between", gap:16, alignItems:"center", flexWrap:"wrap", marginBottom:16 }}>
      <div><div style={{ fontWeight:T.weight.semibold, color:T.color.text }}>Jurisdiction audit</div>
        <div style={{ fontSize:T.size.sm, color:T.color.textMuted, marginTop:3 }}>AI proposals never serve as evidence until you approve them. Principles require individual AR approval.</div></div>
      <div style={{ display:"flex", gap:8 }}><Button variant="primary" onClick={audit} disabled={!!busy}>{busy === "audit" ? "Tagging…" : "Tag unreviewed"}</Button>
        <Button onClick={approveSelected} disabled={!!busy || !Object.values(selected).some(Boolean)}>{busy === "bulk" ? "Approving…" : "Approve selected precedents"}</Button></div>
    </div>
    {msg && <div style={{ padding:"10px 12px", marginBottom:12, background:T.color.surfaceHover, color:T.color.textSecondary, fontSize:T.size.sm }}>{msg}</div>}
    {rows == null ? <div style={{ padding:30, color:T.color.textMuted }}>Loading…</div> : list.map((r, idx) => {
      const stamp = r.basis_audit || {};
      const text = r.type === "principle" ? `${r.title || ""} — ${r.body || ""}` : (r.canonical || [r.correction, r.cause, r.complaint].filter(Boolean).join(" — "));
      return <div key={`${r.type}:${r.id}`} style={{ display:"grid", gridTemplateColumns:"28px 105px 190px minmax(240px,1fr) 120px", gap:10, alignItems:"start", padding:"12px 0", borderTop:idx ? `1px solid ${T.color.border}` : "none" }}>
        <div>{r.type === "precedent" && <input type="checkbox" checked={!!selected[r.id]} onChange={e => setSelected(s => ({ ...s, [r.id]:e.target.checked }))} />}</div>
        <div><Tag>{r.type}</Tag><div style={{ fontSize:T.size.caption, marginTop:5, color:r.basis_approved ? T.color.include : T.color.textFaint }}>{r.basis_approved ? "AR approved" : "unapproved"}</div></div>
        <Select size="sm" value={r.basis_category || ""} onChange={e => save(r, false, e.target.value)} options={[{ value:"", label:"Unclassified" }, ...BRAIN_BASIS_CATEGORIES.map(c => ({ value:c, label:c }))]} />
        <div><div style={{ fontSize:T.size.sm, color:T.color.text }}>{text || "(blank)"}</div><div style={{ fontSize:T.size.caption, color:T.color.textMuted, marginTop:4 }}>{stamp.confidence || "—"} · {stamp.why || "No proposal yet"}</div></div>
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          <Button size="sm" variant="primary" disabled={!!busy || !r.basis_category || r.basis_approved} onClick={() => save(r, true)}>{busy === r.id ? "Saving…" : "Approve"}</Button>
          {r.basis_approved && <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => save(r, false)}>Revoke</Button>}
        </div>
      </div>;
    })}
  </Card>;
}

/* [C5 2026-06-10] Principles — distilled human-approved heuristics injected into the classifier (only when approved). */
function PrinciplesSection({ onChanged }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState("");
  const [title, setTitle] = useState(""); const [body, setBody] = useState(""); const [lean, setLean] = useState("");
  const [adding, setAdding] = useState(false);
  const [suggesting, setSuggesting] = useState(false); const [drafts, setDrafts] = useState([]); const [suggestMsg, setSuggestMsg] = useState("");
  const load = async () => { setErr(""); try { const r = await brainFetch("list-principles"); setRows(r.rows || []); } catch (e) { setErr(e.message); setRows([]); } };
  useEffect(() => { load(); }, []);
  // [#7] distill candidate principles from clusters of confirmed precedents (grouped by category).
  // Drafts are shown for review; admin clicks "Add as draft" → they enter the list below → admin Approves.
  const suggest = async () => {
    setSuggesting(true); setSuggestMsg(""); setDrafts([]);
    try {
      const r = await brainFetch("list", { type: "precedent" });
      const precs = (r.rows || []).filter(p => p.decision === "Include" || p.decision === "Exclude");
      const byCat = {}; for (const p of precs) { const c = p.category || "qualifying"; (byCat[c] ||= []).push(p); }
      const cats = Object.entries(byCat).filter(([, arr]) => arr.length >= 3).sort((a, b) => b[1].length - a[1].length).slice(0, 8);
      if (!cats.length) { setSuggestMsg("Need ≥3 confirmed precedents in a category to distill. Confirm/approve more lines first."); setSuggesting(false); return; }
      const all = [];
      for (const [cat, arr] of cats) {
        const cluster = arr.slice(0, 40).map(p => ({ repair: p.canonical || [p.correction, p.cause, p.complaint].filter(Boolean).join(" — "), decision: p.decision, rationale: p.rationale || "" }));
        try { const out = await claudeDistill({ category: cat, precedents: cluster }); if (Array.isArray(out)) for (const d of out) all.push({ ...d, category: cat }); } catch (e) { /* skip a failed cluster, keep going */ }
      }
      setDrafts(all);
      setSuggestMsg(all.length ? `Drafted ${all.length} candidate${all.length !== 1 ? "s" : ""} from ${cats.length} categor${cats.length !== 1 ? "ies" : "y"}. Review and add the good ones.` : "No clear principles emerged from the current clusters.");
    } catch (e) { setSuggestMsg("Suggest failed: " + e.message); }
    setSuggesting(false);
  };
  const addDraft = async (d, idx) => {
    try { await brainFetch("add-principle", { title: d.title || "", body: d.body || "", decisionLean: d.decisionLean || null, source: `ai-distilled:${d.category || ""}`, reviewer: "admin" }); setDrafts(prev => prev.filter((_, i) => i !== idx)); await load(); onChanged && onChanged(); }
    catch (e) { setSuggestMsg("Add failed: " + e.message); }
  };
  const add = async () => {
    if (!title.trim() && !body.trim()) return;
    setAdding(true);
    try { await brainFetch("add-principle", { title: title.trim(), body: body.trim(), decisionLean: lean || null, reviewer: "admin" }); setTitle(""); setBody(""); setLean(""); await load(); onChanged && onChanged(); }
    catch (e) { setErr(e.message); }
    setAdding(false);
  };
  const setStatus = async (id, status) => { setBusyId(id); try { await brainFetch("set-principle", { id, status, reviewer: "admin" }); await load(); onChanged && onChanged(); } catch (e) { setErr(e.message); } setBusyId(""); };
  const list = rows || [];
  return (
    <div>
      <div style={{ fontSize: T.size.sm, color: T.color.textMuted, marginBottom: T.space[5] }}>Distilled human-approved heuristics. Only <b>approved</b> principles are injected into the classifier prompt (self-gating). Keep them general + decisive.</div>
      <Card style={{ marginBottom: T.space[6] }}>
        <div style={{ display: "grid", gap: 8 }}>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title — e.g. 'Comebacks / dealer rework'" />
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={2} placeholder="Body — the heuristic, e.g. 'A repair correcting the dealer's own prior incomplete work (no part failure) is Exclude — human intervention.'" style={{ width: "100%", boxSizing: "border-box", background: T.color.surface, border: `1px solid ${T.color.border}`, borderRadius: T.radius.md, padding: "9px 12px", color: T.color.text, fontFamily: T.font.sans, fontSize: T.size.sm, resize: "vertical" }} />
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ width: 190 }}><Select value={lean} onChange={e => setLean(e.target.value)} options={[{ value: "", label: "Leans… (optional)" }, { value: "Include", label: "Leans Include" }, { value: "Exclude", label: "Leans Exclude" }, { value: "Human Review", label: "Leans Human Review" }]} /></div>
            <Button variant="primary" onClick={add} disabled={adding || (!title.trim() && !body.trim())}>{adding ? "Adding…" : "Add as draft"}</Button>
          </div>
        </div>
      </Card>
      <Card style={{ marginBottom: T.space[6] }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220, fontSize: T.size.sm, color: T.color.textMuted }}>Let the brain propose principles from your <b>confirmed</b> precedents (grouped by category). Suggestions are drafts only — nothing affects the classifier until you Approve it below.</div>
          <Button variant="primary" onClick={suggest} disabled={suggesting}>{suggesting ? "Distilling…" : "✨ Suggest from precedents"}</Button>
        </div>
        {suggestMsg && <div style={{ marginTop: 8, fontSize: T.size.sm, color: T.color.textMuted }}>{suggestMsg}</div>}
        {drafts.length > 0 && <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
          {drafts.map((d, i) => (
            <div key={i} style={{ border: `1px dashed ${T.color.accent}`, borderRadius: T.radius.lg, padding: "10px 12px", background: T.color.accentSoft }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 3, flexWrap: "wrap" }}>
                <Tag color={T.color.accent}>✨ {d.category}</Tag>
                {d.decisionLean && <Tag color={T.color.accent}>leans {d.decisionLean}</Tag>}
                {d.confidence && <Tag color={d.confidence === "high" ? T.color.include : d.confidence === "low" ? T.color.exclude : T.color.review}>{d.confidence}</Tag>}
                {d.title && <span style={{ fontWeight: T.weight.semibold, fontSize: T.size.sm, color: T.color.text }}>{d.title}</span>}
              </div>
              <div style={{ fontSize: T.size.sm, color: T.color.textSecondary, lineHeight: T.leading.snug }}>{d.body}</div>
              {d.basis && <div style={{ fontSize: T.size.caption, color: T.color.textFaint, marginTop: 3 }}>basis: {d.basis}</div>}
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                <Button variant="secondary" onClick={() => addDraft(d, i)}>+ Add as draft</Button>
                <Button variant="ghost" onClick={() => setDrafts(prev => prev.filter((_, j) => j !== i))}>Dismiss</Button>
              </div>
            </div>
          ))}
        </div>}
      </Card>
      {err && <div style={{ color: T.color.exclude, fontSize: T.size.sm, marginBottom: 10 }}>⚠ {err}</div>}
      {rows == null ? <div style={{ color: T.color.textMuted, fontSize: T.size.sm }}>Loading…</div>
        : list.length === 0 ? <div style={{ padding: 40, textAlign: "center", color: T.color.textMuted, fontSize: T.size.sm }}>No principles yet — add one above.</div>
        : list.map(p => (
          <div key={p.id} style={{ border: `1px solid ${T.color.border}`, borderRadius: T.radius.lg, padding: "12px 14px", marginBottom: 8, background: T.color.surface, display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 3, flexWrap: "wrap" }}>
                <Tag color={p.status === "approved" ? T.color.include : p.status === "rejected" ? T.color.exclude : T.color.textMuted}>{p.status}</Tag>
                {p.decision_lean && <Tag color={T.color.accent}>leans {p.decision_lean}</Tag>}
                {p.title && <span style={{ fontWeight: T.weight.semibold, color: T.color.text, fontSize: T.size.sm }}>{p.title}</span>}
              </div>
              <div style={{ fontSize: T.size.sm, color: T.color.textSecondary, lineHeight: T.leading.snug }}>{p.body}</div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {p.status !== "approved" && <Button variant="secondary" onClick={() => setStatus(p.id, "approved")} disabled={busyId === p.id}>Approve</Button>}
              {p.status !== "rejected" && <Button variant="ghost" onClick={() => setStatus(p.id, "rejected")} disabled={busyId === p.id}>Reject</Button>}
            </div>
          </div>
        ))}
    </div>
  );
}

function BrowseSection({ type, onChanged }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [dec, setDec] = useState("all");
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setErr(""); setRows(null);
    try { const r = await brainFetch("list", { type }); setRows(r.rows || []); }
    catch (e) { setErr(e.message); setRows([]); }
  }, [type]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this brain entry? (A legacy entry will come back if you re-run Backfill.)")) return;
    setBusyId(id);
    try { await brainFetch("delete", { type, id }); setRows(rs => (rs || []).filter(r => r.id !== id)); onChanged && onChanged(); }
    catch (e) { setErr(e.message); }
    setBusyId("");
  };

  let list = rows || [];
  if (type === "precedent" && dec !== "all") list = list.filter(r => r.decision === dec);
  if (q.trim()) {
    const s = q.toLowerCase();
    list = list.filter(r => type === "association"
      ? `${r.associate_correction} ${r.anchor_correction} ${r.rationale}`.toLowerCase().includes(s)
      : `${r.correction} ${r.cause} ${r.complaint} ${r.rationale}`.toLowerCase().includes(s));
  }

  const COLS = type === "association" ? "60px 110px minmax(340px,2fr) minmax(140px,1fr) 40px" : "56px 110px minmax(240px,1.5fr) minmax(180px,1.1fr) 90px 40px";
  const head = type === "association" ? ["Uses", "Relation", "Associate ↔ Anchor", "Why", ""] : ["Uses", "Decision", "Correction", "Why", "Source", ""];

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: T.space[5], alignItems: "center" }}>
        <div style={{ flex: 1 }}><Input value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${type === "association" ? "associations" : "precedents"}…`} leftIcon="⌕" size="sm" /></div>
        {type === "precedent" && <div style={{ width: 170 }}><Select size="sm" value={dec} onChange={e => setDec(e.target.value)} options={[{ value: "all", label: "All decisions" }, { value: "Include", label: "Include" }, { value: "Exclude", label: "Exclude" }, { value: "Human Review", label: "Human Review" }, { value: "Ambiguous", label: "Ambiguous" }]} /></div>}
        <Button variant="ghost" onClick={load}>↺ Reload</Button>
        <span style={{ marginLeft: "auto", fontSize: T.size.caption, color: T.color.textMuted }}>{list.length} shown</span>
      </div>
      {err && <div style={{ fontSize: T.size.caption, color: T.color.exclude, marginBottom: 10 }}>⚠ {err}</div>}
      <div style={{ border: `1px solid ${T.color.border}`, borderRadius: T.radius.lg, overflow: "hidden", background: T.color.surface }}>
        <div style={{ display: "grid", gridTemplateColumns: COLS, borderBottom: `1px solid ${T.color.border}` }}>
          {head.map((c, i) => <div key={i} style={{ padding: "10px 12px" }}><Caps>{c}</Caps></div>)}
        </div>
        {rows == null ? <div style={{ padding: 40, textAlign: "center", color: T.color.textMuted, fontSize: T.size.sm }}>Loading…</div>
          : list.map(r => (
            <div key={r.id} style={{ display: "grid", gridTemplateColumns: COLS, alignItems: "center", borderBottom: `1px solid ${T.color.borderFaint}` }}>
              <div style={{ padding: "10px 12px" }}><Num size={T.size.sm} color={r.support_count > 1 ? T.color.accent : T.color.textMuted}>{r.support_count}</Num></div>
              {type === "association" ? (
                <>
                  <div style={{ padding: "10px 12px" }}><Tag color={r.relation === "associated" ? T.color.include : r.relation === "not" ? T.color.exclude : T.color.review}>{r.relation}</Tag></div>
                  <div style={{ padding: "10px 12px", fontSize: T.size.sm, color: T.color.text, lineHeight: T.leading.snug }}>{r.associate_correction} <span style={{ color: T.color.textFaint }}>↔</span> {r.anchor_correction}</div>
                  <div style={{ padding: "10px 12px", fontSize: T.size.caption, color: T.color.textSecondary }}>{r.rationale}</div>
                </>
              ) : (
                <>
                  <div style={{ padding: "10px 12px" }}>{["Include", "Exclude", "Human Review"].includes(r.decision) ? <Pill status={r.decision === "Include" ? "include" : r.decision === "Exclude" ? "exclude" : "review"} size="sm" /> : <Tag>{r.decision || "—"}</Tag>}</div>
                  <div style={{ padding: "10px 12px", fontSize: T.size.sm, color: T.color.text, lineHeight: T.leading.snug }}>{r.correction || <em style={{ color: T.color.textFaint }}>{(r.cause || r.complaint || "blank").slice(0, 60)}</em>}{r.canonical ? <div style={{ marginTop: 3, fontSize: 10, color: T.color.textFaint, fontFamily: T.font.mono }}>{r.canonical}{r.category && r.category !== "qualifying" ? `  [${r.category}]` : ""}</div> : <div style={{ marginTop: 3, fontSize: 9, color: T.color.review }}>{"not meaning-indexed"}</div>}</div>
                  <div style={{ padding: "10px 12px", fontSize: T.size.caption, color: T.color.textSecondary, lineHeight: T.leading.snug }}>{r.rationale}</div>
                  <div style={{ padding: "10px 12px" }}><Tag>{r.schema_source || "—"}</Tag></div>
                </>
              )}
              <div style={{ padding: "10px 12px" }}><button onClick={() => remove(r.id)} disabled={busyId === r.id} title="Delete entry" style={{ background: "transparent", border: "none", color: T.color.textFaint, cursor: "pointer", fontSize: 14 }}>✕</button></div>
            </div>
          ))}
        {rows != null && list.length === 0 && <div style={{ padding: 40, textAlign: "center", color: T.color.textMuted, fontSize: T.size.sm }}>{rows.length === 0 ? "Empty — run Backfill in Operations." : "No matches."}</div>}
      </div>
    </div>
  );
}
